/**
 * PotentialCard — Potenziale-Kachel (Jun-2026, User-Wunsch).
 *
 * Zeigt die offenen, noch nicht abgerechneten Umsatzpositionen
 * ("Umsatz, der noch auf der Straße liegt") als Tages-Kennzahl und
 * springt per Klick direkt in den Auswertungs-Tab „Potenziale".
 * Quelle: GET /dealer/analytics/potential.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Target, ChevronRight, Coins } from "lucide-react";
import api from "../../lib/api";

const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const num = (v) => Number(v || 0).toLocaleString("de-DE");

export default function PotentialCard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/dealer/analytics/potential")
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => { if (alive) setData({ summary: { positions: 0, amount_gross: 0 }, categories: [] }); });
        return () => { alive = false; };
    }, []);

    if (!data) return null;
    const s = data.summary || {};
    const go = (key) => navigate(`/dealer/analytics?tab=potential${key ? `&focus=${key}` : ""}`);

    return (
        <button type="button" onClick={() => go()}
            className="group block w-full text-left bg-white border border-gray-200 rounded-sm p-4 transition-colors hover:border-[#7C3AED]/50 hover:bg-violet-50/40"
            data-testid="potential-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-[#7C3AED]" /> Potenziale — offener, noch nicht abgerechneter Umsatz
                </h3>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#7C3AED] group-hover:underline">
                    Zur Auswertung <ChevronRight className="w-3.5 h-3.5" />
                </span>
            </div>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
                <div>
                    <div className="font-display font-black text-2xl text-gray-900" data-testid="potential-card-positions">{num(s.positions)}</div>
                    <div className="text-[11px] text-gray-500">offene Positionen</div>
                </div>
                <div>
                    <div className="font-display font-black text-2xl text-emerald-700 flex items-center gap-1.5" data-testid="potential-card-amount">
                        <Coins className="w-5 h-5 text-emerald-500" /> {eur(s.amount_gross)}
                    </div>
                    <div className="text-[11px] text-gray-500">Potenzial (brutto) – „liegt noch auf der Straße"</div>
                </div>
            </div>
            {(data.categories || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="potential-card-chips">
                    {data.categories.filter((c) => c.count > 0).map((c) => (
                        <span key={c.key} className="text-[11px] rounded-full px-2.5 py-1 bg-gray-100 text-gray-600">
                            {c.label}: <b className="text-gray-800">{num(c.count)}</b>
                        </span>
                    ))}
                </div>
            )}
        </button>
    );
}
